import { CellClickedEvent } from 'ag-grid-community';
import { union } from 'lodash';
import React, { ComponentType, FC, useCallback, useMemo } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { ProjectId, TaskIdV2 } from 'api/models';
import {
  ColumnsProps,
  SingleClickEditProps,
  startEditing,
} from 'components/atoms/grid';
import { TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { isChildNode } from 'utils/is-child-node';
import { taskColumn } from './task-column';

type OwnProps = {
  onTaskClick?: (id: TaskIdV2, projectId: ProjectId) => void;
  fields: Record<number, TaskField>;
};

export const withTaskColumn = <T extends SingleClickEditProps & ColumnsProps>(
  Component: ComponentType<T>,
): FC<T & OwnProps> => {
  const Next: FC<T & OwnProps> = ({
    onTaskClick,
    fields,
    columns: actualColumns,
    onCellClick: onClicked,
    ...props
  }) => {
    const onCellClicked = useCallback(
      (event: CellClickedEvent) => {
        const handled = onClicked?.(event);

        if (handled) {
          return true;
        }

        if (event.colDef.field !== 'status') {
          return false;
        }

        const target = event.event?.target as HTMLElement;

        if (isChildNode(['task-field-value'])(target)) {
          startEditing(event);
        } else {
          event.api.clearFocusedCell();
          const task = event.data as Task;
          const projectId = ProjectId(task.project);
          const taskId = TaskIdV2(task.taskId);

          onTaskClick?.(taskId, projectId);
        }

        return true;
      },
      [onTaskClick, onClicked],
    );

    const columns = useMemo(
      () =>
        actualColumns.length === 0
          ? actualColumns
          : union([taskColumn(fields)], actualColumns),
      [fields, actualColumns],
    );

    return (
      <Component
        {...(props as any)}
        onCellClick={onCellClicked}
        columns={columns}
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithTaskColumn'))(Next);
};
